import axios from 'axios';
import { inject, injectable } from 'tsyringe';
import { BaseApi } from '../BaseApi';
import { MiddlewareInjectToken } from '../constants/tokens';
import { Apis, MiddlewareConfig } from '../types';
import {
    HoopsAiArticle,
    HoopsAiArticleWithExtraInfo,
    NewsArticle,
    PassportAvailableLanguages,
} from '../types/News.type';
import { formatInsightNews, formatOrbisNews, formatPassportNews } from '../utils';

@injectable()
export class NewsApi extends BaseApi {
    constructor(
        @inject(MiddlewareInjectToken.Apis) apis: Apis,
        @inject(MiddlewareInjectToken.Config) config: MiddlewareConfig,
    ) {
        super(apis, config);
    }

    async getFeaturedPassportItems(language?: PassportAvailableLanguages): Promise<NewsArticle[]> {
        const featuredIds: number[] = (await axios.get('https://www.pws.io/api/slider_articles')).data.slider_articles;
        const responses = await Promise.all(
            featuredIds.map((id: number) =>
                this.apis.insight.passport.passportArticleById({
                    ID: id,
                    language: language ?? 'eng',
                }),
            ),
        );

        return responses.map((item) => formatPassportNews(item.data));
    }

    async getPassportNews(params?: {
        symbols?: string[];
        limit?: number;
        tag?: string;
        tagGroup?: string;
        releasedBefore?: number;
        releasedAfter?: number;
        language?: PassportAvailableLanguages;
        type?: string;
    }): Promise<NewsArticle[]> {
        const { symbols, limit, tag, tagGroup, releasedBefore, releasedAfter, language, type } = params || {};
        const response = await this.apis.insight.passport.passportArticles({
            language: language || 'eng',
            symbols: symbols?.filter((symbol) => symbol).join(','),
            count: limit || 10,
            tag: tag,
            tag_group: tagGroup,
            released_after: releasedAfter,
            released_before: releasedBefore,
            type,
        });

        return response.data.map((article) => formatPassportNews(article));
    }

    async getPassportNewsById(id: number, language?: PassportAvailableLanguages): Promise<NewsArticle> {
        const response = await this.apis.insight.passport.passportArticleById({
            ID: id,
            language: language ?? 'eng',
        });
        return formatPassportNews(response.data);
    }

    async getOrbisNewsById(id: number): Promise<NewsArticle> {
        const response = await this.apis.orbis.research.news.lookup(`${id}`);
        return formatOrbisNews(response.data);
    }

    async getInsightsNewsFiltered({
        symbols = [],
        endDate,
        paging,
        startDate,
        channels = [],
        tags = [],
    }: {
        symbols?: string[];
        endDate?: Date;
        paging?: {
            limit: number;
            offset: number;
        };
        startDate?: Date;
        channels?: string[];
        tags?: string[];
    }): Promise<NewsArticle[]> {
        const response = await this.apis.insight.news.newsFiltered({
            end_date: endDate?.toISOString(),
            paging: paging ?? {
                limit: 10,
                offset: 0,
            },
            start_date: startDate?.toISOString(),
            channels,
            symbols: symbols.filter((symbol) => symbol),
            tags,
        });

        return response.data.news.map(formatInsightNews);
    }

    async getInsightNewsArticleById(id: string): Promise<NewsArticle> {
        const response = await this.apis.insight.news.newsById({ id });
        return formatInsightNews(response.data);
    }

    async getInsightsNewsTags(): Promise<string[]> {
        const response = await this.apis.insight.news.newsAvailableTags({});
        return response.data;
    }

    async getInsightsNewsChannels(symbol?: string): Promise<string[]> {
        const response = await this.apis.insight.news.newsAvailableChannels({ symbol });
        return response.data;
    }

    async getHoopsAiNews(symbols: string[]): Promise<HoopsAiArticleWithExtraInfo[]> {
        const symbolsList = symbols.join(',');
        const response = await this.apis.middleware.getApiClient().get<{
            result: (Record<string, HoopsAiArticle> | HoopsAiArticle) & { missing_symbol_list?: string[] };
        }>(`v1/hoopsai/v1/resources/finance/daily/${symbolsList}`, {
            secure: true,
            params: {
                images_enabled: true,
                separate_header: true,
                doc_group: 'Stocks_Daily',
            },
        });

        const result = response.data.result;
        delete result['missing_symbol_list'];

        return symbols.length === 1
            ? [{ ...(result as HoopsAiArticle), id: symbols[0]! }]
            : Object.entries(result).map(([id, article]) => ({
                  ...article,
                  id, //We need to use the symbol as an identifier
              }));
    }
}
